import React, { useState, useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import './ProfileEditor.css';
import { interpolateBSpline } from 'lamp-core';

const ProfileLine = ({ points, viewWidth, viewHeight, viewCenterX, viewCenterY, tubularSegments }) => {
  const lineRef = useRef();
  const mirrorRef = useRef();
  const constructionLineRef = useRef();
  
  // Convert normalized x value to absolute diameter value
  const normalizedToDiameter = (x) => {
    return (x - 0.5) * viewWidth;  // Convert normalized x (0-1) to actual diameter (-125mm to +125mm)
  };
  
  useEffect(() => {
    if (lineRef.current && mirrorRef.current && constructionLineRef.current && points.length > 1) {
      // Generate interpolated points using B-spline with segments matching tubular resolution
      const interpolatedPoints = interpolateBSpline(points, tubularSegments);
      
      // Calculate positions for the right side (editable)
      const rightPositions = [];
      for (let i = 0; i < interpolatedPoints.length - 1; i++) {
        const point = interpolatedPoints[i];
        const nextPoint = interpolatedPoints[i + 1];
        const diameter = normalizedToDiameter(point.x);
        const nextDiameter = normalizedToDiameter(nextPoint.x);
        rightPositions.push(
          diameter,  // Use absolute diameter value directly
          point.y * viewHeight,
          0,
          nextDiameter,  // Use absolute diameter value directly
          nextPoint.y * viewHeight,
          0
        );
      }
      
      // Calculate positions for the left side (mirrored)
      const leftPositions = [];
      for (let i = 0; i < interpolatedPoints.length - 1; i++) {
        const point = interpolatedPoints[i];
        const nextPoint = interpolatedPoints[i + 1];
        const diameter = normalizedToDiameter(point.x);
        const nextDiameter = normalizedToDiameter(nextPoint.x);
        leftPositions.push(
          -diameter,  // Mirror the absolute diameter value
          point.y * viewHeight,
          0,
          -nextDiameter,  // Mirror the absolute diameter value
          nextPoint.y * viewHeight,
          0
        );
      }
      
      // Calculate positions for construction lines (right side only)
      const constructionRightPositions = points.slice(0, -1).flatMap((point, i) => {
        const diameter = normalizedToDiameter(point.x);
        const nextDiameter = normalizedToDiameter(points[i + 1].x);
        return [
          diameter,  // Use absolute diameter value directly
          point.y * viewHeight,
          0,
          nextDiameter,  // Use absolute diameter value directly
          points[i + 1].y * viewHeight,
          0
        ];
      });
      
      // Update B-spline curve
      lineRef.current.geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(rightPositions, 3)
      );
      lineRef.current.geometry.attributes.position.needsUpdate = true;

      mirrorRef.current.geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(leftPositions, 3)
      );
      mirrorRef.current.geometry.attributes.position.needsUpdate = true;

      // Update construction lines
      constructionLineRef.current.geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(constructionRightPositions, 3)
      );
      constructionLineRef.current.geometry.attributes.position.needsUpdate = true;
    }
  }, [points, viewWidth, viewHeight, viewCenterX, viewCenterY, tubularSegments]);

  if (points.length <= 1) return null;

  return (
    <>
      {/* B-spline curve (right side) */}
      <lineSegments ref={lineRef}>
        <bufferGeometry>
          <float32BufferAttribute
            attach="attributes-position"
            count={(points.length - 1) * 2}
            array={new Float32Array(points.slice(0, -1).flatMap((point, i) => {
              const diameter = normalizedToDiameter(point.x);
              const nextDiameter = normalizedToDiameter(points[i + 1].x);
              return [
                diameter,  // Use absolute diameter value directly
                point.y * viewHeight,
                0,
                nextDiameter,  // Use absolute diameter value directly
                points[i + 1].y * viewHeight,
                0
              ];
            }))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#e67e22" linewidth={4} />
      </lineSegments>

      {/* B-spline curve (left side) */}
      <lineSegments ref={mirrorRef}>
        <bufferGeometry>
          <float32BufferAttribute
            attach="attributes-position"
            count={(points.length - 1) * 2}
            array={new Float32Array(points.slice(0, -1).flatMap((point, i) => {
              const diameter = normalizedToDiameter(point.x);
              const nextDiameter = normalizedToDiameter(points[i + 1].x);
              return [
                -diameter,  // Mirror the absolute diameter value
                point.y * viewHeight,
                0,
                -nextDiameter,  // Mirror the absolute diameter value
                points[i + 1].y * viewHeight,
                0
              ];
            }))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#e67e22" linewidth={4} opacity={0.5} transparent={true} />
      </lineSegments>

      {/* Construction lines (right side only) */}
      <lineSegments ref={constructionLineRef}>
        <bufferGeometry>
          <float32BufferAttribute
            attach="attributes-position"
            count={(points.length - 1) * 2}
            array={new Float32Array(points.slice(0, -1).flatMap((point, i) => {
              const diameter = normalizedToDiameter(point.x);
              const nextDiameter = normalizedToDiameter(points[i + 1].x);
              return [
                diameter,  // Use absolute diameter value directly
                point.y * viewHeight,
                0,
                nextDiameter,  // Use absolute diameter value directly
                points[i + 1].y * viewHeight,
                0
              ];
            }))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineDashedMaterial 
          color="#666666" 
          dashSize={5}
          gapSize={5}
          scale={1}
          linewidth={2}
        />
      </lineSegments>
    </>
  );
};

const ProfileEditor = ({ 
  onProfileUpdate, 
  initialPoints = [],
  maxHeight = 250,  // Default value in mm
  maxDiameter = 250,  // Default value in mm
  tubularSegments = 32,  // Default value
  height = 400,  // Default value in pixels
  disabled = false  // Disable interaction when true
}) => {
  const [points, setPoints] = useState(initialPoints.length > 0 ? initialPoints : [
    { x: 1.0, y: 0 },  // Bottom point at y=0
    { x: 1.0, y: 1 }   // Top point at y=1
  ]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const shouldHandleClickRef = useRef(true);
  const canvasRef = useRef(null);
  const lastSentPointsRef = useRef(null);
  const isInternalUpdateRef = useRef(false);
  const lastInitialPointsRef = useRef(null);
  const draggedPointRef = useRef(null); // Track the actual point object being dragged

  // Deep comparison helper for points arrays
  const pointsAreEqual = (points1, points2) => {
    if (!points1 || !points2 || points1.length !== points2.length) {
      return false;
    }
    return points1.every((p1, i) => {
      const p2 = points2[i];
      return Math.abs(p1.x - p2.x) < 0.0001 && Math.abs(p1.y - p2.y) < 0.0001;
    });
  };

  // Add effect to update points when initialPoints changes (only if different from last processed)
  useEffect(() => {
    if (initialPoints.length > 0) {
      // Only update if initialPoints has actually changed from what we last processed
      if (!pointsAreEqual(initialPoints, lastInitialPointsRef.current)) {
        isInternalUpdateRef.current = true;
        lastInitialPointsRef.current = initialPoints.map(p => ({ ...p }));
        // Convert from geometry coordinates to profile editor coordinates
        const profilePoints = convertFromGeometryCoordinates(initialPoints);
        updatePoints(profilePoints);
        // Reset the flag after state update completes
        setTimeout(() => {
          isInternalUpdateRef.current = false;
        }, 0);
      }
    }
  }, [initialPoints]);

  // Keep the ref in sync with state
  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  // Update points
  const updatePoints = (newPoints) => {
    // Ensure bottom point stays fixed at y=0 and top point at y=1
    const normalizedPoints = [...newPoints];
    if (normalizedPoints[0].y !== 0) {
      normalizedPoints[0] = { ...normalizedPoints[0], y: 0 };
    }
    if (normalizedPoints[normalizedPoints.length - 1].y !== 1) {
      normalizedPoints[normalizedPoints.length - 1] = { ...normalizedPoints[normalizedPoints.length - 1], y: 1 };
    }
    setPoints(normalizedPoints);
  };

  // Calculate view bounds based on max dimensions
  const viewWidth = maxDiameter;  // Use actual diameter in mm
  const viewHeight = maxHeight;   // Use actual height in mm

  // Camera viewport dimensions (must match camera settings)
  // Add small padding to show boundaries
  const padding = 10; // 10mm padding on each side
  const cameraWidth = maxDiameter + (padding * 2);  // left: -maxDiameter/2 - padding, right: maxDiameter/2 + padding
  const cameraHeight = maxHeight + (padding * 2); // top: maxHeight + padding, bottom: -padding

  // Convert normalized x value to absolute diameter value
  const normalizedToDiameter = (x) => {
    return (x - 0.5) * maxDiameter;  // Convert normalized x (0-1) to actual diameter (-125mm to +125mm)
  };

  // Convert absolute diameter value to normalized x value
  const diameterToNormalized = (diameter) => {
    return (diameter / maxDiameter) + 0.5;  // Convert actual diameter (-125mm to +125mm) to normalized x (0-1)
  };

  // Convert screen coordinates to world coordinates
  // Uses camera viewport dimensions to properly map screen pixels to world coordinates
  const screenToWorld = (x, y) => {
    const rect = canvasRef.current.getBoundingClientRect();
    // Map screen coordinates to camera viewport coordinates
    const normalizedX = (x - rect.left) / rect.width;
    const normalizedY = (y - rect.top) / rect.height;
    
    // Convert to world coordinates using camera viewport
    // Camera left: -maxDiameter/2 - padding, right: maxDiameter/2 + padding
    const worldX = (normalizedX * cameraWidth) - (cameraWidth / 2);
    // Camera bottom: -padding, top: maxHeight + padding
    // Y is inverted in screen coordinates (top is 0, bottom is height)
    const worldY = (1 - normalizedY) * cameraHeight - padding;
    
    return { x: worldX, y: worldY };
  };

  // Convert world coordinates to normalized coordinates (0-1)
  // Maps world coordinates to the viewWidth/viewHeight area (centered in camera viewport)
  const worldToNormalized = (x, y) => {
    // Map world X to normalized X (0-1)
    // Normalized X: 0 = -maxDiameter/2, 0.5 = 0mm (center axis), 1 = +maxDiameter/2
    // World X is centered at 0, so we add viewWidth/2 to shift it to 0-based, then divide by viewWidth
    // Formula: normalizedX = (worldX + viewWidth/2) / viewWidth
    // When worldX = 0 (center axis): normalizedX = (0 + viewWidth/2) / viewWidth = 0.5 ✓
    const normalizedX = (x + (viewWidth / 2)) / viewWidth;
    
    // Map world Y to normalized Y (0-1)
    // Normalized Y: 0 = 0mm, 1 = maxHeight
    // World Y ranges from -padding to maxHeight + padding
    // We add padding to shift it to 0-based, then divide by viewHeight
    // Formula: normalizedY = (worldY + padding) / viewHeight
    const normalizedY = (y + padding) / viewHeight;
    
    return {
      x: Math.max(0, Math.min(1, normalizedX)),
      y: Math.max(0, Math.min(1, normalizedY))
    };
  };

  // Handle mouse down on a point
  const handlePointPointerDown = (index) => {
    if (disabled) return;
    setSelectedPoint(index);
    setIsDragging(true);
    shouldHandleClickRef.current = true;
    // Store reference to the point being dragged
    draggedPointRef.current = points[index];
  };

  // Handle mouse down on canvas
  const handleMouseDown = (event) => {
    if (disabled) return;
    if (event.button === 0) { // Left click
      const { x, y } = screenToWorld(event.clientX, event.clientY);
      const normalized = worldToNormalized(x, y);
      
      // Check if click is near any existing point
      const clickedPointIndex = points.findIndex((point, i) => {
        if (i === 0 || i === points.length - 1) return false; // Skip first and last points
        const dx = point.x - normalized.x;
        const dy = point.y - normalized.y;
        return Math.sqrt(dx * dx + dy * dy) < 0.1;
      });

      if (clickedPointIndex !== -1) {
        setSelectedPoint(clickedPointIndex);
        setIsDragging(true);
        shouldHandleClickRef.current = true;
        // Store reference to the point being dragged
        draggedPointRef.current = points[clickedPointIndex];
      }
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (disabled) return;
    setIsDragging(false);
    setSelectedPoint(null);
    draggedPointRef.current = null;
  };

  // Handle point dragging
  const handlePointDrag = (event) => {
    if (disabled) return;
    if (!isDraggingRef.current || selectedPoint === null) return;

    const { x, y } = screenToWorld(event.clientX, event.clientY);
    const normalized = worldToNormalized(x, y);

    // Update the selected point
    const newPoints = [...points];
    const draggedPoint = newPoints[selectedPoint];
    
    // Update the dragged point's position
    // Clamp x to prevent crossing the center axis (0.5) - points can only be on the right side
    draggedPoint.x = Math.max(0.5, Math.min(1, normalized.x));
    draggedPoint.y = Math.max(0, Math.min(1, normalized.y));
    
    // Store reference to the dragged point before sorting
    draggedPointRef.current = draggedPoint;

    // Sort points by y-coordinate (but preserve object references)
    newPoints.sort((a, b) => {
      // If points have the same y, maintain relative order for the dragged point
      if (Math.abs(a.y - b.y) < 0.0001) {
        // If one is the dragged point, it should come after the other at the same y
        if (a === draggedPoint) return 1;
        if (b === draggedPoint) return -1;
      }
      return a.y - b.y;
    });
    
    // Find the new index of the dragged point by reference
    const newIndex = newPoints.findIndex(p => p === draggedPoint);
    
    setSelectedPoint(newIndex);
    updatePoints(newPoints);
  };

  // Handle adding a new point
  const handleAddPoint = (event) => {
    if (disabled) return;
    if (!shouldHandleClickRef.current) return;
    
    const { x, y } = screenToWorld(event.clientX, event.clientY);
    const normalized = worldToNormalized(x, y);
    
    // Don't add points too close to existing ones
    const tooClose = points.some(point => {
      const dx = point.x - normalized.x;
      const dy = point.y - normalized.y;
      return Math.sqrt(dx * dx + dy * dy) < 0.1;
    });

    if (!tooClose) {
      // Clamp x to prevent crossing the center axis (0.5) - points can only be on the right side
      const clampedX = Math.max(0.5, Math.min(1, normalized.x));
      const newPoints = [...points, { x: clampedX, y: normalized.y }];
      newPoints.sort((a, b) => a.y - b.y);
      updatePoints(newPoints);
    }
  };

  // Handle deleting a point
  const handleDeletePoint = (index) => {
    if (index === 0 || index === points.length - 1) return; // Can't delete first or last point
    const newPoints = points.filter((_, i) => i !== index);
    updatePoints(newPoints);
  };

  // Convert profile editor coordinates (0-1, where 0.5 is center) to geometry coordinates (0-1, where 0 is center)
  // Profile editor: 0 = left edge, 0.5 = center, 1 = right edge
  // Geometry: 0 = center, 1 = edge (right side only, left is mirrored)
  // The geometry does: x = point.x * (diameter / 2), so:
  //   - point.x = 0 → radius = 0 (center)
  //   - point.x = 1 → radius = diameter/2 (edge)
  // Conversion: For right side (normalizedX >= 0.5): geometryX = (normalizedX - 0.5) * 2
  //   This maps: 0.5 → 0 (center), 1 → 1 (edge) ✓
  // For left side (normalizedX < 0.5): mirror to right side equivalent
  //   geometryX = (0.5 - normalizedX) * 2, which gives the same distance from center
  const convertToGeometryCoordinates = (profilePoints) => {
    return profilePoints.map(point => {
      let geometryX;
      if (point.x >= 0.5) {
        // Right side: convert directly
        geometryX = (point.x - 0.5) * 2; // Maps 0.5 → 0, 1 → 1
      } else {
        // Left side: mirror to right side (same distance from center)
        geometryX = (0.5 - point.x) * 2; // Maps 0 → 1, 0.5 → 0
      }
      return {
        x: Math.max(0, Math.min(1, geometryX)),
        y: point.y
      };
    });
  };

  // Convert geometry coordinates (0-1, where 0 is center) to profile editor coordinates (0-1, where 0.5 is center)
  // Reverse of convertToGeometryCoordinates
  // Geometry: 0 = center, 1 = edge
  // Profile editor: 0.5 = center, 1 = edge (right side)
  // Conversion: profileX = (geometryX / 2) + 0.5
  //   This maps: 0 → 0.5 (center), 1 → 1 (edge) ✓
  // Clamp to ensure points are always on the right side (>= 0.5)
  const convertFromGeometryCoordinates = (geometryPoints) => {
    return geometryPoints.map(point => ({
      x: Math.max(0.5, Math.min(1, (point.x / 2) + 0.5)), // Maps 0 → 0.5, 1 → 1, clamped to >= 0.5
      y: point.y
    }));
  };

  // Notify parent component of profile changes (only if points actually changed and not from internal update)
  useEffect(() => {
    if (onProfileUpdate && !isInternalUpdateRef.current) {
      // Only call onProfileUpdate if points have actually changed
      if (!pointsAreEqual(points, lastSentPointsRef.current)) {
        lastSentPointsRef.current = points.map(p => ({ ...p }));
        // Convert profile editor coordinates to geometry coordinates before sending
        const geometryPoints = convertToGeometryCoordinates(points);
        onProfileUpdate(geometryPoints);
      }
    }
  }, [points, onProfileUpdate]);

  return (
    <>
      <div 
        className="profile-canvas"
        ref={canvasRef}
        style={{ height: `${height}px`, background: 'var(--color-bg-dark)' }}
      >
        <Canvas 
          camera={{ 
            position: [0, 0, 5],
            zoom: 1,
            left: -(maxDiameter / 2) - padding,  // Left edge: -maxDiameter/2 - padding
            right: (maxDiameter / 2) + padding,   // Right edge: maxDiameter/2 + padding
            top: maxHeight + padding,            // Top edge: maxHeight + padding
            bottom: -padding,                     // Bottom edge: -padding
            near: 0.1,
            far: 1000
          }}
          orthographic
          style={{ background: '#1a1a1a' }}
          dpr={[1, 2]}
          gl={{ 
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true,
            powerPreference: "high-performance"
          }}
          onPointerDown={handleMouseDown}
          onPointerUp={handleMouseUp}
          onPointerMove={handlePointDrag}
          onClick={handleAddPoint}
        >
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} />
          
          {/* Draw the points */}
          {points.map((point, index) => {
            const diameter = normalizedToDiameter(point.x);
            return (
              <mesh
                key={index}
                position={[
                  diameter,  // Use absolute diameter value directly
                  point.y * viewHeight,
                  0
                ]}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  handlePointPointerDown(index);
                }}
                onPointerUp={(e) => {
                  e.stopPropagation();
                }}
                onContextMenu={(e) => {
                  if (e.nativeEvent) {
                    e.nativeEvent.preventDefault();
                  }
                  handleDeletePoint(index);
                }}
              >
                <sphereGeometry args={[4, 16, 16]} />
                <meshBasicMaterial 
                  color={selectedPoint === index ? "#ffffff" : index === 0 ? "#666666" : "#E4572E"} 
                />
              </mesh>
            );
          })}

          {/* Draw lines between points */}
          <ProfileLine 
            points={points}
            viewWidth={viewWidth}
            viewHeight={viewHeight}
            viewCenterX={0}
            viewCenterY={0}
            tubularSegments={tubularSegments}
          />

          {/* Draw the revolution axis */}
          <line>
            <bufferGeometry>
              <float32BufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([
                  0, -50, 0,  // Start 50 units below the bottom
                  0, viewHeight + 50, 0  // Extend 50 units above the top
                ])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineDashedMaterial 
              color="#888888" 
              dashSize={5}
              gapSize={5}
              scale={1}
            />
          </line>

          {/* Draw dimension labels */}
          <Html position={[viewWidth + 10, 0, 0]}>
            <div style={{ color: '#888', fontSize: '12px' }}>
              {maxDiameter}mm
            </div>
          </Html>
        </Canvas>
      </div>
      
      <div className="profile-controls">
        <div className="button-group">
          <button onClick={() => {
            const defaultPoints = [{ x: 1.0, y: 0 }, { x: 1.0, y: 1 }];
            updatePoints(defaultPoints);
            setSelectedPoint(null);
          }}>
            Reset Profile
          </button>
        </div>
      </div>
    </>
  );
};

export default ProfileEditor; 